import { replyWithAttachmentLastModifiedText } from './attachButtonMenuItemBuilderUtils.locstring.json';
import loc, { format } from 'owa-localize';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { AttachmentFile, AttachmentFileType } from 'owa-attachment-file-types';
import { AttachmentConstants } from 'owa-attachment-model-store/lib/utils/attachmentConstants';
import { lazyCreateCloudFileFromAttachmentData } from 'owa-attachment-suggestions';
import { ClientAttachmentId } from 'owa-client-ids';
import { owaDate, OwaDate, formatUserDateTime } from 'owa-datetime';
import { ComposeViewState } from 'owa-mail-compose-store';
import { lazyCreateAttachments } from 'owa-mail-compose-actions';
import { ReplyWithAttachmentSuggestion, ReplyWithEndpointType } from 'owa-mail-store';

import { trace } from 'owa-trace';

const TRUNCATION_SEPARATOR: string = '...';
const MAX_FILENAME_LENGTH: number = 35;

export async function onAttachmentClicked(
    suggestion: ReplyWithAttachmentSuggestion,
    suggestionIndex: number,
    composeViewState: ComposeViewState
): Promise<void> {
    if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
        const attachmentId: string = suggestion.Id;

        const attachmentFile: AttachmentFile = {
            fileType: AttachmentFileType.AttachmentItemSuggestion,
            name: suggestion.FileName,
            size: parseInt(suggestion.FileSize),
            attachmentItemId: attachmentId,
            type: AttachmentConstants.attachmentTypes.ITEM_ID_ATTACHMENT_TYPE,
        };

        const createAttachments = await lazyCreateAttachments.import();
        createAttachments(
            [attachmentFile],
            composeViewState,
            {
                isInline: false,
                isHiddenInline: false,
                shouldShare: false,
            },
            null /* processInlineImageBeforeUploadFunc */,
            null /* attachmentInfoBarAddHandler */,
            null /* attachmentInfoBarRemoveHandler */,
            (attachmentId: ClientAttachmentId) =>
                onAttachmentUploaded(attachmentId.Id, suggestion, suggestionIndex)
        );
    } else {
        const createCloudFileFromAttachmentData = await lazyCreateCloudFileFromAttachmentData.import();
        const createAttachments = await lazyCreateAttachments.import();
        await createCloudFileFromAttachmentData(suggestion, cloudFile => {
            createAttachments(
                [cloudFile] /* files */,
                composeViewState,
                {
                    isInline: false,
                    isHiddenInline: false,
                    shouldShare: true,
                },
                null /* processInlineImageBeforeUploadFunc */,
                null /* attachmentInfoBarAddHandler */,
                null /* attachmentInfoBarRemoveHandler */,
                (attachmentId: ClientAttachmentId) =>
                    onAttachmentUploaded(attachmentId.Id, suggestion, suggestionIndex)
            );
        });
    }
}

export async function onAttachmentUploaded(
    generatedAttachmentId: string,
    suggestion: ReplyWithAttachmentSuggestion,
    suggestionIndex: number
): Promise<void> {
    logUsage('AttachmentSuggestionClicked', [
        suggestion.Endpoint,
        suggestion.Id,
        suggestionIndex,
        suggestion.FileSourceType,
        suggestion.FileExtension,
        generatedAttachmentId,
    ]);
}

/**
 * For cloudy attachments, we are parsing a sharepoint or onedrive URL.
 * For classic attachments, we are parsing an email folder path ('\Inbox\Address to me' for example)
 */
export function getBreadcrumbedFileOrEmailPath(suggestion: ReplyWithAttachmentSuggestion): string {
    let breadcrumbedFileOrEmailPath: string = null;

    // Protect all logic with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
            // Just display the name of the email containing the attachment.
            breadcrumbedFileOrEmailPath = suggestion.SourceTitle;
        } else {
            // To parse URL for cloudy attachment, create temporary anchor tag.
            const tempAnchorTag: HTMLAnchorElement = document.createElement('a');
            tempAnchorTag.href = suggestion.Url;

            // Hostname should be something like sharepoint.microsoft.com
            // Pathname should be something like /documents/file.docx
            // Splitting the pathname using / as the delimiter, we can ignore the first and last tokens,
            // as the file path does not include the name of the file itself.
            breadcrumbedFileOrEmailPath = decodeURIComponent(tempAnchorTag.hostname);
            const isRTL: boolean = getRTL();

            const pathTokens: string[] = decodeURIComponent(tempAnchorTag.pathname).split('/');
            for (let i = 1; i < pathTokens.length - 1; i++) {
                if (isRTL) {
                    // RTL
                    breadcrumbedFileOrEmailPath =
                        pathTokens[i] + ' < ' + breadcrumbedFileOrEmailPath;
                } else {
                    // LTR
                    breadcrumbedFileOrEmailPath += ' > ' + pathTokens[i];
                }
            }
        }
    } catch (e) {
        trace.info(
            '[breadcrumbedFileOrEmailPath] Failed to parse attachment data, message: ' + e.message
        );
    } finally {
        return breadcrumbedFileOrEmailPath;
    }
}

export function getTooltipForAttachmentSuggestion(
    suggestion: ReplyWithAttachmentSuggestion
): string {
    let tooltipString: string = null;

    // Protect with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        const date: OwaDate = owaDate('UTC', suggestion.DateModified);
        const lastModifiedDate: string = format(
            loc(replyWithAttachmentLastModifiedText),
            formatUserDateTime(date)
        );

        tooltipString = [
            suggestion.FileName,
            getBreadcrumbedFileOrEmailPath(suggestion),
            lastModifiedDate,
        ].join('\n');
    } catch (e) {
        trace.info(
            '[getTooltipForAttachmentSuggestion] Failed to parse attachment data, message: ' +
                e.message
        );
    } finally {
        return tooltipString;
    }
}

export function fixFileName(suggestion: ReplyWithAttachmentSuggestion): string {
    // If file name contains file extension, remove it. We'll add it separately.
    const fileExtension: string = '.' + suggestion.FileExtension;
    if (suggestion.FileName.indexOf(fileExtension) >= 0) {
        return suggestion.FileName.replace(fileExtension, '');
    } else {
        return suggestion.FileName;
    }
}

export function middleTruncateFileName(fileName: string) {
    // Do not truncate the string if it contains 35 characters or fewer.
    if (fileName.length <= MAX_FILENAME_LENGTH) {
        return fileName;
    } else {
        const charsToShow: number = MAX_FILENAME_LENGTH - TRUNCATION_SEPARATOR.length;
        const frontChars: number = Math.ceil(charsToShow / 2);
        const backChars: number = Math.floor(charsToShow / 2);

        return (
            fileName.substr(0, frontChars) +
            TRUNCATION_SEPARATOR +
            fileName.substr(fileName.length - backChars)
        );
    }
}

export function getFileExtension(suggestion: ReplyWithAttachmentSuggestion) {
    return '.' + suggestion.FileExtension;
}
